// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Delivery.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import ReactDOM from 'react-dom';
import connect from 'share/enhancers/Izenda.Connect';
import { observe } from 'mobx';
import Validator from 'share/components/hoc/Izenda.Validator';
import Dropdown from 'share/components/controls/Izenda.DropDown';
import Input from 'share/components/controls/Izenda.Input';
import ReportSubscriptionConstants from 'logic/data/Izenda.ReportSubscriptionConstants';
import FilterValueSelection from './Izenda.FilterValueSelection';
import Recipient from './Izenda.Recipient';
import IconAfterCursorInput from 'share/components/controls/Izenda.IconAfterCursorInput';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import { Popover } from 'share/components/controls/Izenda.Popover';
import SystemVariables from 'modules/Subscription/Izenda.SystemVariables';
import SystemVariablesUI from 'logic/models/Izenda.SystemVariablesUI';
import SCHEDULE_SEARCH_CONSTANTS from 'logic/data/Izenda.SchedulingSearchConstants';
import { flow, head, get, any, isNil, find, isEqual, filter, pullAt } from 'lodash/fp';
import * as PermissionUtils from 'share/utils/Izenda.PermissionUtils';

const ValidatorDropdown = Validator()(Dropdown);
const UserAccountStore = require('logic/Izenda.UserAccountStore');

@connect()
class Delivery extends Component {
  static propTypes = {
    data: PropTypes.object
  };

  constructor(props: any) {
    super(props);

    this.subData = props.data;
    this.originalDeliveryMethod = this.subData.deliveryMethod;
    this.originalEmailBody = this.subData.emailBody;

    this.disposeDeliveryType = observe(this.subData, "deliveryType", () => {
      this.observeDeliveryType();
    });

    this.disposeDeliveryMethod = observe(this.subData, "deliveryMethod", value => {
      this.observeDeliveryMethod(value);
    });
    this.options = [];
    this.init();
  }

  /**
   * Init data
   */
  init() {
    if (
      this.subData.type === ReportSubscriptionConstants.SUBSCRIPTION_TYPE_VALUES.subscriptionAlert ||
      this.subData.type === SCHEDULE_SEARCH_CONSTANTS.INSTANCE_TYPE_VALUE.SCHEDULE_ALERT
    ) {
      this.subData.deliveryType = "Email";
    }

    const availableDeliveryTypes = this.getDeliveryTypeList();
    const firstTypeValue = flow(head, get("value"))(availableDeliveryTypes);
    const isValidType = any(item => item.value === this.subData.deliveryType, availableDeliveryTypes);
    !isValidType && (this.subData.deliveryType = firstTypeValue);

    this.observeDeliveryType();
    this.observeDeliveryMethod(this.subData.deliveryMethod);
    this.systemVariablesUI = new SystemVariablesUI();

    this.userAccountStore = new UserAccountStore(this.props.loadUsers, this.props.userModeType);
    this.userAccountStore.getAllUserAccounts().then((userAccounts)=>{
      const listLen = userAccounts.result.length;
      for (let index = 0; index < listLen; index++) {
        const userAccount = userAccounts.result[index];
        this.options.push({emailAddress: userAccount.emailAddress});
      }
    });
  }

  /**
   * Observe delivery type.
   */
  observeDeliveryType() {
    this.loadDeliveryMethod();
    const method = this.subData.deliveryMethod;
    const listMethod = this.deliveryMethods;

    if (listMethod && isNil(find(item => isEqual(item.value, method), listMethod)) && listMethod.length !== 0) {
      this.subData.deliveryMethod = head(this.deliveryMethods).value;
    }

    this.isEmail = this.subData.deliveryType === "Email";
  }

  /**
   * Observe delivery method.
   * @param {string} value
   */
  observeDeliveryMethod(value: string) {
    this.subData.deliveryMethod = value;
    this.loadExportFileTypes();
    this.loadExportAttachmentTypes();

    this.subData.deliveryType === "Email" && this.getEmailBody();
  }

  /**
   * Load delivery method list
   */
  loadDeliveryMethod() {
    const deliveryType = this.subData.deliveryType;
    const matchedDeliveryType = find({value: deliveryType}, ReportSubscriptionConstants.DELIVERY_TYPE);

    if (!matchedDeliveryType)
      return null;

    this.deliveryMethods = PermissionUtils.filterAllowedSubscriptionDeliveryMethods(
      matchedDeliveryType.deliveryMethod,
      this.subData.deliveryType,
      ReportSubscriptionConstants.EXPORT_TYPE
    );
  }

  /**
   * Load export file types dropdownlist
   */
  loadExportFileTypes() {
    this.isShowExportFileTypes = false;
    if (this.subData.deliveryType === "File Location" && this.subData.deliveryMethod === "Send to disk") {
      this.isShowExportFileTypes = true;
      this.exportFileTypes = PermissionUtils.filterAllowedSubscriptionExportFileTypes(
        ReportSubscriptionConstants.EXPORT_TYPE
      );

      if (isNil(this.exportFileTypes) || this.exportFileTypes.length === 0) {
        return;
      }

      if (this.subData.exportFileType) return;

      const pdfType = find(item => item.value === "PDF", this.exportFileTypes);

      if (pdfType) {
        this.subData.exportFileType = pdfType.value;
      } else {
        this.subData.exportFileType = head(this.exportFileTypes).value;
      }
    }
  }

  /**
   * Check delivery email type permission.
   */
  deliveryEmailTypePermission() {
    const { authorization, authorizationConstants: { PERMISSION_KEY: permissionKeys } } = this.props;
    const hasLinkPrivilege = authorization.checkPrivileges([permissionKeys.EmailingDeliveryMethodLink]);
    const hasEmbeddedHTMLPrivilege = authorization.checkPrivileges([permissionKeys.EmailingDeliveryMethodEmbeddedHTML]);
    const hasAttachmentPrivilege = authorization.checkPrivileges([permissionKeys.EmailingDeliveryMethodAttachment]);

    return hasLinkPrivilege || hasEmbeddedHTMLPrivilege || hasAttachmentPrivilege;
  }

  /**
   * onchange export file type dropdown
   * @param {any} dropdown
   */
  onChangeExportFileType = (dropdown: any) => {
    this.subData.updateProperty("exportFileType", dropdown.value);
  };

  /**
   * onchange IsSubscriptionTimeZoneUsed checkbox
   * @param {event} event
   */
  onChangeIsSubscriptionTimeZoneUsed = (event: event) => {
    this.subData.updateProperty("isSubscriptionTimeZoneUsed", event.target.checked);
  };

  /**
   * on change attachment type
   * @param {any} dropdown
   */
  onChangeAttachmentType = (dropdown: any) => {
    this.subData.updateProperty("exportAttachmentType", dropdown.value);
  };

  /**
   * Load export attachment types dropdown
   */
  loadExportAttachmentTypes() {
    this.isShowExportAttachmentTypes = false;
    if (this.subData.deliveryType === "Email" && this.subData.deliveryMethod === "Attachment") {
      this.isShowExportAttachmentTypes = true;
      this.exportAttachmentTypes = PermissionUtils.filterAllowedSubscriptionAttachmentTypes(
        ReportSubscriptionConstants.EXPORT_TYPE
      );

      if (isNil(this.exportAttachmentTypes) || this.exportAttachmentTypes.length === 0) {
        return;
      }

      if (this.subData.exportAttachmentType) return;

      const pdfType = find(item => item.value === "PDF", this.exportAttachmentTypes);

      if (pdfType) {
        this.subData.exportAttachmentType = pdfType.value;
      } else {
        this.subData.exportAttachmentType = head(this.exportAttachmentTypes).value;
      }
    }
  }

  /**
   * get delivery type
   */
  getDeliveryType() {
    return this.subData.deliveryType;
  }

  /**
   * onchange delivery type
   * @param {any} e
   */
  onChangeDeliveryType = (e: any) => {
    this.subData.updateProperty("deliveryType", e.value);
  };

  /**
   * get delivery type list
   * @return {Array} deliveryType
   */
  getDeliveryTypeList() {
    let types = [];
    if (
      this.subData.type === SCHEDULE_SEARCH_CONSTANTS.INSTANCE_TYPE_VALUE.SUBSCRIBE_REPORTING_ITEM ||
      this.subData.type === SCHEDULE_SEARCH_CONSTANTS.INSTANCE_TYPE_VALUE.SCHEDULE_REPORT
    ) {
      types = ReportSubscriptionConstants.DELIVERY_TYPE;
    } else {
      types = pullAt([1], ReportSubscriptionConstants.DELIVERY_TYPE);
    }

    !this.deliveryEmailTypePermission() &&
      (types = filter(item => item.value !== SCHEDULE_SEARCH_CONSTANTS.DELIVERY_TYPE_VALUE.EMAIL, types));
    return types;
  }

  /**
   * get delivery method
   */
  getDeliveryMethod() {
    return this.subData.deliveryMethod;
  }

  /**
   * onchange delivery method dropdown
   * @param {any} e
   */
  onChangeDeliveryMethod = (e: any) => {
    this.subData.updateProperty("deliveryMethod", e.value);
  };

  /**
   * get email subject
   * @return {String} emailSubject
   */
  getEmailSubject() {
    return this.subData.emailSubject;
  }

  /**
   * onchange email subject
   * @param {any} input
   */
  onChangeEmailSubject = (input: any) => {
    this.subData.updateProperty("emailSubject", input.target.value);
  };

  /**
   * get email boy
   */
  getEmailBody() {
    const deliveryMethod = this.subData.deliveryMethod;

    if (this.originalDeliveryMethod === deliveryMethod) {
      this.subData.emailBody = this.originalEmailBody;
      return;
    }

    const matchedBody = find(item => deliveryMethod === item.key, this.subData.emailTemplates);

    this.subData.updateProperty("emailBody", matchedBody && matchedBody.value);
  }

  /**
   * Handle onchange of Email Body
   * @param {any} e
   */
  onChangeEmailBody = (e: any) => {
    this.subData.updateProperty("emailBody", e.target.value);
  };

  componentWillUnmount() {
    this.disposeDeliveryType();
    this.disposeDeliveryMethod();
  }

  /**
   * Show system variable popover for subject.
   * @param {number} position
   */
  showSystemVariablesPopupForSubject = (position: number) => {
    this.showSystemVariablesPopup(ReportSubscriptionConstants.ELEMENTS.EMAIL_SUBJECT.KEY, position);
  };

  /**
   * Show system variable popover for body.
   * @param {number} position
   */
  showSystemVariablesPopupForBody = (position: number) => {
    this.showSystemVariablesPopup(ReportSubscriptionConstants.ELEMENTS.EMAIL_BODY.KEY, position);
  };

  /**
   * Show system variable popover.
   * @param {string} sourceType
   * @param {number} position
   */
  showSystemVariablesPopup(sourceType: string, position: number) {
    const domNode = ReactDOM.findDOMNode(this.refs[sourceType]);
    const scrollBoxValues = this.refs.scrollBox.getValues();
    const left = domNode.offsetParent.offsetLeft + (position && position.left + 20) - scrollBoxValues.scrollLeft;
    const top = domNode.offsetParent.offsetTop + (position && position.top + 150) - scrollBoxValues.scrollTop;
    const $sourceNode =
      sourceType === ReportSubscriptionConstants.ELEMENTS.EMAIL_SUBJECT.KEY
        ? $izenda(ReportSubscriptionConstants.ELEMENTS.EMAIL_SUBJECT.CLASS)
        : $izenda(ReportSubscriptionConstants.ELEMENTS.EMAIL_BODY.CLASS);
    const cursorPosition = $izenda($sourceNode).prop("selectionStart");

    const currentVisible = this.systemVariablesUI.visible;

    this.systemVariablesUI.populateData({
      visible: !currentVisible,
      left: left,
      top: top,
      sourceType: sourceType,
      cursorPosition: cursorPosition,
      $sourceNode: $sourceNode
    });
  }

  /**
   * Render system variable popover component.
   */
  renderSystemVariablesPopover() {
    return (
      <Popover
        visible={this.systemVariablesUI.visible}
        positionLeft={this.systemVariablesUI.left}
        positionTop={this.systemVariablesUI.top}
      >
        <SystemVariables store={this.props.store} onSelect={this.onSelectSystemVariable} />
      </Popover>
    );
  }

  /**
   * Handle select system variable.
   * @param {string} name
   */
  onSelectSystemVariable = (name: string) => {
    const sourceValue =
      this.systemVariablesUI.sourceType === ReportSubscriptionConstants.ELEMENTS.EMAIL_SUBJECT.KEY
        ? this.subData.emailSubject
        : this.subData.emailBody;
    const sourceKey =
      this.systemVariablesUI.sourceType === ReportSubscriptionConstants.ELEMENTS.EMAIL_SUBJECT.KEY ? "emailSubject" : "emailBody";

    const cursorPos = this.systemVariablesUI.cursorPosition;
    const textBefore = sourceValue.substring(0, cursorPos);
    const textAfter = sourceValue.substring(cursorPos, sourceValue.length);
    this.subData.updateProperty(sourceKey, textBefore + name + textAfter);

    this.systemVariablesUI.updateVisible(false);
    this.systemVariablesUI.$sourceNode.focus();
  };

  /**
   * render export file type
   */
  renderExportFileType() {
    if (this.isShowExportFileTypes) {
      return (
        <div className="form-group">
          <div className="col-md-3 control-label">
            {this.props.localization.getText("SUBSCRIPTION_LABEL_EXPORT_FILE_TYPE")}
          </div>
          <div className="col-md-9">
            <ValidatorDropdown
              options={this.exportFileTypes}
              value={this.subData.exportFileType}
              onChange={this.onChangeExportFileType}
            />
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * render IsSubscriptionTimeZoneUsed checkbox
   */
  renderIsSubscriptionTimeZoneUsed(){
    const isShowIsSubscriptionTimeZoneUsed = this.subData.deliveryType === "Email" &&
          (this.subData.deliveryMethod === "Attachment" || this.subData.deliveryMethod === "Embedded HTML");
    if(isShowIsSubscriptionTimeZoneUsed)
    {
      const ValidatorInput = Validator()(Input);
      return(
        <div className="form-group">
          <div className="col-md-3 control-label">
            {this.props.localization.getText("SUBSCRIPTION_LABEL_USE_SUBSCRIPTION_TIME_ZONE")}
          </div>
          <div className="col-md-9">
            <ValidatorInput
              checked={this.subData.isSubscriptionTimeZoneUsed}
              onChange={this.onChangeIsSubscriptionTimeZoneUsed}
              className='form-control'
              type="checkbox"
            />
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * render export attachment type dropdownlist
   */
  renderExportAttachmentType() {
    if (this.isShowExportAttachmentTypes) {
      return (
        <div className="form-group">
          <div className="col-md-3 control-label">
            {this.props.localization.getText("SUBSCRIPTION_LABEL_EXPORT_ATTACHMENT_TYPE")}
          </div>
          <div className="col-md-9">
            <ValidatorDropdown
              options={this.exportAttachmentTypes}
              value={this.subData.exportAttachmentType}
              onChange={this.onChangeAttachmentType}
            />
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * render email subject textbox
   */
  renderEmailSubject() {
    if (this.isEmail) {
      return (
        <div className="form-group">
          <div className="col-md-3 control-label">
            {this.props.localization.getText("SUBSCRIPTION_LABEL_EMAIL_SUBJECT")}
          </div>
          <div className="col-md-9">
            <IconAfterCursorInput
              type="input"
              value={this.getEmailSubject()}
              className="form-control email-subject"
              onChange={this.onChangeEmailSubject}
              onClickIcon={this.showSystemVariablesPopupForSubject}
              ref={ReportSubscriptionConstants.ELEMENTS.EMAIL_SUBJECT.KEY}
            />
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * render email body textbox
   */
  renderEmailBody() {
    if (this.isEmail) {
      return (
        <div className="form-group">
          <div className="col-md-3 control-label">
            {this.props.localization.getText("SUBSCRIPTION_LABEL_EMAIL_BODY")}
          </div>
          <div className="col-md-9">
            <IconAfterCursorInput
              value={this.subData.emailBody}
              rows="10"
              cols="50"
              className="form-control email-body"
              onChange={this.onChangeEmailBody}
              onClickIcon={this.showSystemVariablesPopupForBody}
              ref={ReportSubscriptionConstants.ELEMENTS.EMAIL_BODY.KEY}
            />
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * render recipient component
   */
  renderRecipient() {
    if (this.isEmail) {
      return !this.subData.isSubscription ? <Recipient {...this.props} options={this.options} /> : null;
    }
    return null;
  }

  /**
   * render component
   */
  render() {
    const localization = this.props.localization;

    return (
      <div className="izenda-SubscriptionDesigner-Container">
        <ScrollBox ref="scrollBox">
          <div className="form-horizontal schedule">
            <div className="text-center izenda-text-bold u_marginBottom">
              {localization.getText("SUBSCRIPTION_LABEL_DELIVERY_RECIPIENT")}
            </div>
            <div className="form-group">
              <div className="col-md-3 control-label">{localization.getText("SUBSCRIPTION_LABEL_DELIVERY_TYPE")}</div>
              <div className="col-md-9">
                <ValidatorDropdown
                  options={this.getDeliveryTypeList()}
                  labelField="label"
                  valueField="value"
                  value={this.getDeliveryType()}
                  onChange={this.onChangeDeliveryType}
                  field="DeliveryType"
                  validations={ReportSubscriptionConstants.CONTRAINTS.DELIVERY_TYPE}
                />
              </div>
            </div>
            <div className="form-group">
              <div className="col-md-3 control-label">{localization.getText("COMMON_LABEL_DELIVERY_METHOD")}</div>
              <div className="col-md-9">
                <ValidatorDropdown
                  options={this.deliveryMethods}
                  value={this.getDeliveryMethod()}
                  onChange={this.onChangeDeliveryMethod}
                  field="DeliveryMethod"
                  validations={ReportSubscriptionConstants.CONTRAINTS.DELIVERY_METHOD}
                />
              </div>
            </div>
            {this.renderExportFileType()}
            {this.renderExportAttachmentType()}
            {this.renderIsSubscriptionTimeZoneUsed()}
            {this.renderRecipient()}
            {this.renderEmailSubject()}
            {this.renderEmailBody()}
            <hr />
            <FilterValueSelection {...this.props} />
          </div>
        </ScrollBox>
        {this.renderSystemVariablesPopover()}
      </div>
    );
  }
}

export default Delivery;



// WEBPACK FOOTER //
// ./ui/modules/Subscription/Izenda.Delivery.js